import { setAtatusUser } from '@/utils/atatus';
import AxiosService, { API_STATUS_CODE } from './Service';
import { URLS, buildAPIURL } from '@/utils/url';
import { ERROR_MESSAGE, NOTIFICATION_TYPE, setToastNotification } from '@/redux/action/ToastNotificationAction';
import {
  LOCALSTORAGE_KEY,
  setLocalStorageByKey,
  getLocalStorageByKey,
  isUserAuthenticated,
  getMVToken,
  customAPI,
  setMVToken,
  setMVADToken,
  setMVRefresh,
} from '@/utils/auth';
import { setHeaderPermission, setLoginUser, setUserAzurAD } from '@/redux/action/auth';
import { getAllUsers } from '@/redux/action/user';

export const fetchDataOnInitialLogin = (dispatch: Function) => {
  dispatch(getAllUsers());
};

export const fetchToken = async (
  code: string,
  dispatch: Function,
  callback: (_: null, isError: boolean) => void
) => {
  const result = await AxiosService({
    method: 'post',
    url: buildAPIURL(URLS.FetchToken),
    body: {
      code,
      origin: process.env.REACT_APP_ORIGIN_URL,
    },
    headers: {
      isPublic: true,
    },
  });

  if (!result.response) {
    dispatch(setToastNotification({ type: NOTIFICATION_TYPE.ERROR, message: ERROR_MESSAGE }));
    callback(null, true);
    return;
  }

  if (result.response.responseCode !== API_STATUS_CODE.SUCCESS) {
    dispatch(setToastNotification({ type: NOTIFICATION_TYPE.ERROR, message: result.response.responseMessage }));
    callback(null, true);
    return;
  }

  const { responseData } = result.response;
  const loggedInUser = responseData?.userData?.contact?.[0] ?? {};
  loggedInUser.id = responseData?.userData?.user?.[0]?.id;
  setMVToken(responseData?.access_token);
  setMVADToken(responseData?.ad_access_token);
  setMVRefresh(responseData?.refresh_token);
  setLocalStorageByKey(LOCALSTORAGE_KEY.TOKEN, responseData.access_token);
  setLocalStorageByKey(LOCALSTORAGE_KEY.REFRESH_TOKEN, responseData.refresh_token);
  dispatch(setHeaderPermission(responseData?.userData ?? {}));
  dispatch(setLoginUser(loggedInUser));
  dispatch(setUserAzurAD(responseData?.userData?.userAzurAD?.[0] ?? {}));
  setAtatusUser(loggedInUser.email || loggedInUser.id);
  fetchDataOnInitialLogin(dispatch);
  callback(null, false);
};

export const fetchRefreshToken = async (
  callback: (_: null, isError: boolean) => void
) => {
  const result = await AxiosService({
    method: 'post',
    url: buildAPIURL(URLS.FetchRefreshToken),
    body: {
      refreshToken: getLocalStorageByKey(LOCALSTORAGE_KEY.REFRESH_TOKEN),
    },
    headers: {
      isPublic: true,
    },
  });
  if (result.response && result.response.responseCode === API_STATUS_CODE.SUCCESS) {
    const { responseData } = result.response;
    if (!responseData?.access_token) {
      callback(null, true);
      return;
    }
    setLocalStorageByKey(LOCALSTORAGE_KEY.TOKEN, responseData.access_token);
    setMVToken(responseData.access_token);
    if (responseData.refresh_token) {
      setLocalStorageByKey(LOCALSTORAGE_KEY.REFRESH_TOKEN, responseData.refresh_token);
      setMVRefresh(responseData.refresh_token);
    }
    callback(null, false);
  } else {
    callback(null, true);
  }
};

/**
 * Exchanges a Maverick JWT for the user's session by calling the SSO
 * authenticate endpoint, then persists the returned tokens and hydrates the
 * redux auth state (permissions, logged-in user, Azure AD identity).
 *
 * Shared by the SSO login flow and the Salesforce Canvas embed handoff, which
 * both need to bootstrap a session from an already-issued token rather than an
 * OAuth code.
 */
export const bootstrapUserFromToken = async (
  token: string,
  dispatch: Function,
  callback: (_: null, isError: boolean) => void,
  { clearAdTokenWhenAbsent = false }: { clearAdTokenWhenAbsent?: boolean } = {}
) => {
  let result;
  try {
    result = await customAPI(
      'post',
      `${process.env.REACT_APP_API_BASE_URL}${buildAPIURL(URLS.FetchTokenSSO)}`,
      { origin: process.env.REACT_APP_ORIGIN_URL },
      {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }
    );
  } catch {
    // A network failure or non-JSON response would otherwise reject and skip the callback,
    // leaving the caller (e.g. the embed route) stuck on a loader. Fail closed instead.
    callback(null, true);
    return;
  }

  if (result.responseCode !== API_STATUS_CODE.SUCCESS) {
    callback(null, true);
    return;
  }

  const { responseData } = result;
  // A success envelope without an access token would persist `undefined` into auth storage and
  // break later auth checks — fail closed instead (mirrors fetchRefreshToken's guard).
  if (!responseData?.access_token) {
    callback(null, true);
    return;
  }
  const loggedInUser = responseData?.userData?.contact?.[0] ?? {};
  loggedInUser.id = responseData?.userData?.user?.[0]?.id;
  setMVToken(responseData.access_token);
  setLocalStorageByKey(LOCALSTORAGE_KEY.TOKEN, responseData.access_token);
  // A Canvas handoff creates a fresh session even when this origin still has an old idle marker.
  // Refresh it before initial API calls so the interceptor does not immediately log the user out.
  setLocalStorageByKey(LOCALSTORAGE_KEY.LAST_API_TIMESTAMP, new Date().toISOString());
  // Persist the refresh token when present; otherwise clear any stale one so a later refresh
  // can't run with a previous user's/session's token (a rebuilt session may not carry one).
  if (responseData.refresh_token) {
    setMVRefresh(responseData.refresh_token);
    setLocalStorageByKey(LOCALSTORAGE_KEY.REFRESH_TOKEN, responseData.refresh_token);
  } else {
    setMVRefresh('');
    localStorage.removeItem(LOCALSTORAGE_KEY.REFRESH_TOKEN);
  }
  // Persist the AD token when present. When absent, only CLEAR it if the caller asked: OSL's
  // rebuilt session is assembled from the user record and never carries an ad_access_token, and
  // this function is shared with ordinary SSO rehydration. Clearing unconditionally would blank a
  // valid AD cookie for a user whose session was merely rebuilt after an OSL restart — and since
  // userAzurAD then dispatches as {}, the layout's Knock provider would mount half-initialised
  // rather than forcing the Azure re-login that used to fix it.
  if (responseData.ad_access_token) {
    setMVADToken(responseData.ad_access_token);
  } else if (clearAdTokenWhenAbsent) {
    setMVADToken('');
  }
  dispatch(setHeaderPermission(responseData?.userData ?? {}));
  dispatch(setLoginUser(loggedInUser));
  dispatch(setUserAzurAD(responseData?.userData?.userAzurAD?.[0] ?? {}));
  setAtatusUser(loggedInUser.email || loggedInUser.id);
  fetchDataOnInitialLogin(dispatch);
  callback(null, false);
};

export const fetchTokenSSO = async (
  dispatch: Function,
  callback: (_: null, isError: boolean) => void
) => {
  if (process.env.REACT_APP_SSO_ENABLED !== 'true') {
    callback(null, true);
    return;
  }

  const isUserAuth: any = isUserAuthenticated();
  if (isUserAuth) {
    callback(null, true);
    return;
  }

  const token: any = getMVToken();
  if (!token) {
    callback(null, true);
    return;
  }

  await bootstrapUserFromToken(token, dispatch, callback);
};

export const postError = async (errorStackJSONString: string) => {
  await AxiosService({
    method: 'post',
    url: buildAPIURL(URLS.PostFrontendError),
    body: errorStackJSONString,
  });
};
